// src/components/documents/useDocumentLineTax.js — taxlines5-a4c81e37 (tax codes on every line, Stage S5)
//
// Everything an editor needs for its tax when the company's Settings › Taxes switch is ON, from the live
// settings/taxCodes doc (documentLineTax.js has the rules). Used by the four older pop-ups (estimate,
// invoice, job, recurring invoice); the full pages inherit it. When the switch is off (every real company
// today) `taxOn` is false and every other value is null / 0 — the editor runs its own code, untouched.
//   kind: 'quote' | 'invoice' | 'job' | 'recurring'   source: the stored document (null when new)
// → { taxMode, taxOn, taxDocLoading, taxCodesDoc, ctx, result, totals, taxNote, taxPick, fieldMarks, word, taxCtx }
import { useMemo } from 'react';
import { useTaxCodesDoc } from '../../utils/taxExemptionUi';
import {
    pageTaxMode, TAX_MODES, onModeContext, documentTotals, taxChangeNote, jobStoredTax, canPickLineTax,
    isOfficeTaxRole, fieldTaxCheckCount, DOCUMENT_TAX_KINDS,
} from '../../utils/documentLineTax';

export default function useDocumentLineTax({ orgId, companySettings, taxOptions, taxLoaded, customer, role, lines, source, kind, currency, word: wordIn }) {
    const taxCodesDoc = useTaxCodesDoc(orgId);
    const taxMode = pageTaxMode({ orgId, taxCodesDoc, qbConnected: companySettings?.quickbooksConnected === true });
    const taxOn = taxMode !== TAX_MODES.OFF;
    const taxDocLoading = taxOn && taxCodesDoc === undefined;
    const word = wordIn || (DOCUMENT_TAX_KINDS[kind] || DOCUMENT_TAX_KINDS.quote).word; // the document's own word ("estimate" / "quote")
    const taxCtx = useMemo(() => ({ companySettings, taxOptions, taxLoaded }), [companySettings, taxOptions, taxLoaded]);
    const ctx = useMemo(() => (taxOn
        ? onModeContext({ mode: taxMode, orgId, companySettings, taxOptions, taxLoaded, taxCodesDoc, customer })
        : null), [taxOn, taxMode, orgId, companySettings, taxOptions, taxLoaded, taxCodesDoc, customer]);
    const result = useMemo(() => (ctx ? documentTotals(lines, ctx, source, kind) : null), [ctx, lines, source, kind]);
    // The older-document note (not for recurring schedules, which store no tax).
    const taxNote = useMemo(() => {
        if (!ctx || !result || !source || !source.id || kind === 'recurring') return null;
        const stored = kind === 'job' ? { id: source.id, taxAmount: jobStoredTax(source, source.lineItems) } : source;
        const storedLinesTax = documentTotals(source.lineItems || [], ctx, source, kind).totals.taxAmount;
        return taxChangeNote({ source: stored, storedLinesTax, newTax: result.totals.taxAmount, currency, docWordLower: word });
    }, [ctx, result, source, kind, currency, word]);
    const taxPick = ctx ? { ctx, canPick: canPickLineTax(role), showFieldMarker: isOfficeTaxRole(role) } : undefined;
    const fieldMarks = ctx && isOfficeTaxRole(role) ? fieldTaxCheckCount(lines) : 0;
    return { taxMode, taxOn, taxDocLoading, taxCodesDoc, ctx, result, totals: result ? result.totals : null, taxNote, taxPick, fieldMarks, word, taxCtx };
}
